var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, Fragment, useCallback, useMemo, useContext } from "react";
import { Grid, TextField, ListItemText } from "@material-ui/core";
import { Save, Cancel } from "@material-ui/icons";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { SecondaryAppBar } from "../common/SecondaryAppBar";
import FormSection from "../common/FormSection";
import { qualificationReferencesServiceSearch, qualificationReferencesService, sourcesService, titlesService, complaintTypesService, awardingOrganisationsService, awardingOrganisationsServiceSearch, peopleAffectedService, centreNumberTypeService, exceptionalCircumstanceService, gqVtqService, } from "../../services";
import { SearchListField } from "../common/SearchListField";
import { SelectListField } from "../common/SelectListField";
import { submitComplaint } from "../../redux/actions";
import { useDispatch } from "react-redux";
import { useNavigate } from "../../hooks/useNavigate";
import { useFormState } from "../../hooks/useFormState";
import { searchStringToParams } from "../../utils/urlHelper";
import { HistoryContext } from "../../redux/historyContext";
import { isValidEmail } from "../../utils/validationHelper";
import { isFutureDate } from "../../utils/dateUtils";
const DATE_FORMAT = "dd/MM/yyyy";
const MULTILINE_ROW_HEIGHT = 4;
const WB_MALPRACTICE_COMPLAINT_TYPES = ["whistleblower", "malpractice"];
const ARRAY_PROP_NAMES = ["contacts", "caseAreas"];
const VALIDATION_RULES = [
    "title",
    "source",
    "caseType",
    [
        "receiptDate",
        ({ state, val }) => {
            if (!val) {
                return "Required";
            }
            if (isFutureDate(val)) {
                return "Date must be in the past";
            }
        },
    ],
    "caseType",
    "contacts.firstName",
    "contacts.lastName",
    [
        "contacts.email",
        ({ val }) => {
            if (val) {
                return isValidEmail(val) ? "" : "Please enter a valid email address";
            }
            return "";
        },
    ],
    "whatWentWrong",
    [
        "caseAreas.centreNumberType",
        ({ state, val, arrayIndex }) => {
            const centreNumber = state.values[`caseAreas/${arrayIndex}/centreNumber`];
            return !val && centreNumber
                ? "Centre Number Type must be completed if specifying a Centre Number"
                : "";
        },
    ],
    [
        "caseAreas.centreNumber",
        ({ state, val, arrayIndex }) => {
            const numberType = state.values[`caseAreas/${arrayIndex}/centreNumberType`];
            const valAsString = (val || "");
            if (numberType === "NCN" && valAsString.length !== 5) {
                return "Centre Number must be 5 digits for NCN Numbers";
            }
            if (numberType === "UKPRN" && valAsString.length !== 8) {
                return "Centre Number must be 8 digits for UKPRN Numbers";
            }
            return "";
        },
    ],
];
const FORM_DEFAULTS = {
    status: "Active",
    stage: "triage",
    contacts: [{ primary: true }],
    caseAreas: [{}],
};
export const NewComplaint = ({ returnRoute }) => {
    const { location } = useContext(HistoryContext);
    const { caseType: caseTypeId } = searchStringToParams(location.search);
    const assesIsWbMalpracticeMode = (caseType) => WB_MALPRACTICE_COMPLAINT_TYPES.some(typeId => typeId === caseType);
    const [complaintTypeLabel, setComplaintTypeLabel] = useState("");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    useEffect(() => {
        if (!caseTypeId) {
            navigate(returnRoute);
        }
        else {
            complaintTypesService.get(caseTypeId).then(complaintType => {
                if (complaintType) {
                    setComplaintTypeLabel(complaintType["name"]);
                }
                else {
                    navigate(returnRoute);
                }
            });
        }
    }, [caseTypeId, navigate, returnRoute]);
    const submit = useCallback(({ model }) => __awaiter(void 0, void 0, void 0, function* () {
        if (model.exceptionalCircumstance) {
            const ex = yield exceptionalCircumstanceService.get(model.exceptionalCircumstance);
            if (ex) {
                model.exceptionalCircumstanceCategory = ex.group;
            }
        }
        dispatch(submitComplaint(model));
    }), [dispatch]);
    const initialModel = useMemo(() => (Object.assign({}, FORM_DEFAULTS)), []);
    const { state, formProps, triggerSubmit } = useFormState(Object.assign(Object.assign({}, initialModel), { caseType: caseTypeId, receiptDate: null }), VALIDATION_RULES, submit, false, ARRAY_PROP_NAMES);
    const { propName: contactPropName } = state.arrayMeta.contacts[0];
    const { propName: aocPropName } = state.arrayMeta.caseAreas[0];
    return (React.createElement(Fragment, null,
        React.createElement(SecondaryAppBar, { title: React.createElement(Fragment, null,
                "/ New \"",
                complaintTypeLabel,
                "\" Case"), actions: [
                {
                    title: "Cancel",
                    onClick: () => navigate(returnRoute),
                    startIcon: React.createElement(Cancel, null),
                    "data-test": "new-complaint-cancel",
                },
                {
                    title: "Save",
                    onClick: () => triggerSubmit(),
                    startIcon: React.createElement(Save, null),
                    "data-test": "new-complaint-save",
                },
            ] }),
        React.createElement("form", { style: { marginTop: "50px" } },
            React.createElement(FormSection, { title: "Case Details" },
                React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("title"), { fullWidth: true, label: "Case Title", "data-test": "case-title-field", inputProps: { maxLength: 200 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(SelectListField, Object.assign({}, formProps("source"), { label: "Source", dataTest: "source-list", service: sourcesService }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(KeyboardDatePicker, Object.assign({ inputVariant: "outlined" }, formProps("receiptDate"), { label: "Receipt Date", "data-test": "reciept-date-picker", format: DATE_FORMAT, KeyboardButtonProps: {
                            "aria-label": "change date",
                        }, autoOk: true, fullWidth: true }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(SelectListField, Object.assign({}, formProps("caseType"), { label: "Case Type", dataTest: "case-type-list", service: complaintTypesService }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(SelectListField, Object.assign({}, formProps("exceptionalCircumstance"), { label: "Exceptional Circumstance", "data-test": "exceptional-circumstance-list", service: exceptionalCircumstanceService }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(SelectListField, Object.assign({}, formProps("gqVtq"), { label: "GQ/VTQ", dataTest: "gq-vtq-list", service: gqVtqService })))),
            React.createElement(FormSection, { title: "Contact Details" },
                React.createElement(Grid, { item: true, xs: 12, sm: 1 },
                    React.createElement(SelectListField, Object.assign({}, formProps(contactPropName("contactTitle")), { label: "Title", dataTest: "title-list", service: titlesService }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 2 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(contactPropName("firstName")), { label: "First Name", "data-test": "firstName-field", fullWidth: true, inputProps: { maxLength: 100 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 3 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(contactPropName("lastName")), { label: "Surname", "data-test": "lastName-field", fullWidth: true, inputProps: { maxLength: 100 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 6 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(contactPropName("email")), { label: "Email Address", "data-test": "email-field", fullWidth: true, inputProps: { maxLength: 200 } })))),
            React.createElement(FormSection, { title: "Area of complaint" },
                React.createElement(Grid, { item: true, xs: 12, sm: 6 },
                    React.createElement(SearchListField, Object.assign({}, formProps(aocPropName("awardingOrganisation")), { label: "Awarding Organisation", "data-test": "ao-list", service: {
                            get: id => awardingOrganisationsService.get(id.toString()),
                            getAll: params => awardingOrganisationsServiceSearch.getAll(params),
                        }, renderItem: item => (React.createElement(ListItemText, { primary: item["name"], secondary: `
                          ${item["id"] || ""} 
                          ` })) }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 6 },
                    React.createElement(SearchListField, Object.assign({}, formProps(aocPropName("qualificationReference")), { label: "Qualification Reference", "data-test": "qual-ref-list", service: {
                            get: id => qualificationReferencesService.get(id.toString()),
                            getAll: params => qualificationReferencesServiceSearch.getAll({
                                query: params.query,
                                filter: state.values[aocPropName("awardingOrganisation")],
                            }),
                        }, renderItem: item => (React.createElement(ListItemText, { primary: item["name"], secondary: `
                          ${item["type"] || ""} 
                          ${item["ssa"]}` })) }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 2 },
                    React.createElement(SelectListField, Object.assign({}, formProps(aocPropName("centreNumberType")), { label: "Centre Number Type", "data-test": "centre-no-type-list", service: centreNumberTypeService }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 2 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(aocPropName("centreNumber")), { label: "Centre Number", "data-test": "centre-no-field", fullWidth: true, inputProps: { maxLength: 20 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(aocPropName("centreName")), { label: "Centre Name", "data-test": "centre-name-field", fullWidth: true, inputProps: { maxLength: 100 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(aocPropName("centreAddressLine1")), { label: "Centre Address Line 1", "data-test": "centre-address-line-1-field", fullWidth: true, inputProps: { maxLength: 100 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(aocPropName("centreAddressLine2")), { label: "Centre Address Line 2", "data-test": "centre-address-line-2-field", fullWidth: true, inputProps: { maxLength: 100 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(aocPropName("centreAddressCity")), { label: "Centre Town or City", "data-test": "centre-address-town-field", fullWidth: true, inputProps: { maxLength: 100 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 4 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps(aocPropName("centreAddressPostCode")), { label: "Centre Post code", "data-test": "centre-address-postcode-field", fullWidth: true, inputProps: { maxLength: 20 } })))),
            React.createElement(FormSection, { title: "Summary of complaint" },
                React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("summary"), { label: "Summary", "data-test": "summary-of-complaint-field", fullWidth: true, multiline: true, rows: MULTILINE_ROW_HEIGHT, inputProps: { maxLength: 1000 } }))),
                React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                    React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("whatWentWrong"), { label: "What do you think went wrong?", "data-test": "what-do-you-think-went-wrong-field", fullWidth: true, multiline: true, rows: MULTILINE_ROW_HEIGHT, inputProps: { maxLength: 1000 } }))),
                assesIsWbMalpracticeMode(state.values.caseType) && (React.createElement(Fragment, null,
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("whoInvolved"), { label: "Who is involved?", "data-test": "who-is-involved-field", fullWidth: true, multiline: true, rows: MULTILINE_ROW_HEIGHT, inputProps: { maxLength: 1000 } }))),
                    React.createElement(Grid, { item: true, xs: 12, sm: 6 },
                        React.createElement(SelectListField, Object.assign({}, formProps("peopleAffected"), { label: "How many people has this affected?", "data-test": "how-many-people-affected-field", service: peopleAffectedService }))))),
                !assesIsWbMalpracticeMode(state.values.caseType) && (React.createElement(Fragment, null,
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("howAffected"), { label: "How has this affected you?", "data-test": "how-has-this-affected-you-field", fullWidth: true, multiline: true, rows: MULTILINE_ROW_HEIGHT, inputProps: { maxLength: 1000 } }))),
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("remediation"), { label: "What do you think needs to happen to put things right?", "data-test": "how-has-this-affected-you-field", fullWidth: true, multiline: true, rows: MULTILINE_ROW_HEIGHT, inputProps: { maxLength: 1000 } })))))))));
};
